import { useState, useEffect, useRef } from 'react';
import { Calendar, Share2, Search } from 'lucide-react';
import { motion } from 'framer-motion';
import marketingPlaner from "@/assets/images/marketing/seo.webp";
import socialPlan from "@/assets/images/marketing/all-plan.webp";
import SEO from "@/assets/images/marketing/omh.webp";

export default function MarketingSections() {
    const [activeSection, setActiveSection] = useState('marketing');
    const [isSticky, setIsSticky] = useState(false);
    const sectionsRef = useRef({});
    const navRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY + window.innerHeight / 2;
            const sections = ['marketing', 'social', 'seo'];

            for (const section of sections) {
                const element = sectionsRef.current[section];
                if (element) {
                    const { top, bottom } = element.getBoundingClientRect();
                    const absoluteTop = top + window.scrollY;
                    const absoluteBottom = bottom + window.scrollY;
                    if (scrollPosition >= absoluteTop && scrollPosition < absoluteBottom) {
                        setActiveSection(section);
                        break;
                    }
                }
            }

            const marketingSection = sectionsRef.current['marketing'];
            const seoSection = sectionsRef.current['seo'];

            if (marketingSection && seoSection) {
                const marketingTop = marketingSection.getBoundingClientRect().top + window.scrollY;
                const seoBottom = seoSection.getBoundingClientRect().bottom + window.scrollY;
                const currentScroll = window.scrollY;
                const marketingNavTop = marketingTop - 100;

                if (currentScroll >= marketingNavTop + 50 && currentScroll <= seoBottom - window.innerHeight / 2) {
                    setIsSticky(true);
                } else {
                    setIsSticky(false);
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (sectionId) => {
        const element = sectionsRef.current[sectionId];
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const sections = [
        {
            id: 'marketing',
            icon: Calendar,
            title: 'Never miss an opportunity',
            subtitle: 'Marketing Planner',
            description: 'Marketing Planner notifies you of 200+ upcoming events to engage more customers. Create tailor-made content from templates to post on social and email.',
            image: SEO,
            gradient: 'from-purple-600 to-blue-600',
            glowColor: 'purple'
        },
        {
            id: 'social',
            icon: Share2,
            title: 'Engage with social and email',
            subtitle: 'Social & Email Marketing',
            description: 'Target customers with social content and easy-to-create emails to reinforce your brand.',
            image: socialPlan,
            gradient: 'from-blue-600 to-cyan-600',
            glowColor: 'blue'
        },
        {
            id: 'seo',
            icon: Search,
            title: 'Get found with SEO',
            subtitle: 'Search Engine Optimization',
            description: 'Get your site Google-ready to help customers find your business.',
            image: marketingPlaner,
            gradient: 'from-cyan-600 to-purple-600',
            glowColor: 'cyan'
        }
    ];

    return (
        <div className="min-h-screen relative overflow-hidden">
            {/* Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-72 h-72 bg-purple-600/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>

            {/* Sticky Navigation */}
            <div
                className={`${isSticky ? 'fixed' : 'absolute'} top-4 sm:top-8 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 w-[calc(100%-2rem)] sm:w-auto`}
                ref={navRef}
            >
                <motion.div
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    className="flex items-center bg-gray-900/90 backdrop-blur-xl rounded-full px-2 py-2 border border-purple-500/20 shadow-lg shadow-purple-500/20 w-full sm:w-auto"
                >
                    {sections.map((section) => {
                        const Icon = section.icon;
                        return (
                            <button
                                key={section.id}
                                onClick={() => scrollToSection(section.id)}
                                className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-2.5 sm:py-3 rounded-full font-medium text-xs sm:text-sm transition-all duration-300 ${
                                    activeSection === section.id
                                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                                        : 'text-gray-300 hover:text-white hover:bg-white/5'
                                }`}
                            >
                                {/* Icon only on mobile, icon + text on sm+ */}
                                <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" strokeWidth={2} />
                                <span className="hidden sm:inline truncate">{section.subtitle}</span>
                                {/* Short label on mobile */}
                                <span className="sm:hidden text-[11px] leading-tight text-center">
                                    {section.id === 'marketing' ? 'Planner' : section.id === 'social' ? 'Social' : 'SEO'}
                                </span>
                            </button>
                        );
                    })}
                </motion.div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-4 md:px-6 py-24 sm:py-32 space-y-20 sm:space-y-32 relative z-10">
                {sections.map((section, index) => {
                    const Icon = section.icon;
                    const isReversed = index % 2 !== 0;

                    return (
                        <motion.section
                            key={section.id}
                            ref={el => sectionsRef.current[section.id] = el}
                            initial={{ opacity: 0, y: 60 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="relative"
                        >
                            <div className="relative">
                                {/* Glow Effect */}
                                <div className={`absolute ${isReversed ? 'left-0' : 'right-0'} top-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r ${section.gradient} opacity-10 blur-3xl rounded-full pointer-events-none`}></div>

                                <div className="relative">
                                    <div className={`flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 lg:gap-12 p-5 sm:p-8 lg:p-12`}>

                                        {/* Text Content */}
                                        <div className="flex-1 space-y-4 sm:space-y-6 w-full">
                                            <motion.div
                                                initial={{ scale: 0 }}
                                                whileInView={{ scale: 1 }}
                                                viewport={{ once: true }}
                                                transition={{ delay: 0.4, type: "spring" }}
                                                className="inline-flex"
                                            >
                                                <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-r ${section.gradient} flex items-center justify-center shadow-lg`}>
                                                    <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2} />
                                                </div>
                                            </motion.div>

                                            <div className="space-y-1.5 sm:space-y-2">
                                                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
                                                    {section.title}
                                                </h3>
                                                <p className={`text-base sm:text-lg font-medium text-transparent bg-clip-text bg-gradient-to-r ${section.gradient}`}>
                                                    {section.subtitle}
                                                </p>
                                            </div>

                                            <p className="text-gray-300 text-sm sm:text-base lg:text-lg leading-relaxed max-w-xl">
                                                {section.description}
                                            </p>
                                        </div>

                                        {/* Image */}
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            whileInView={{ opacity: 1, scale: 1 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: 0.3, duration: 0.6 }}
                                            className="flex-1 relative group/image w-full"
                                        >
                                            <div className="relative">
                                                <div className={`absolute inset-0 bg-gradient-to-r ${section.gradient} opacity-20 blur-2xl rounded-3xl group-hover/image:opacity-30 transition-opacity duration-500`}></div>
                                                <div className="relative rounded-2xl overflow-hidden border border-purple-500/20 group-hover/image:border-purple-500/40 transition-all duration-500 shadow-2xl aspect-[4/3] sm:aspect-[4/4]">
                                                    <img
                                                        src={section.image}
                                                        alt={section.title}
                                                        className="w-full h-full object-cover transform group-hover/image:scale-105 transition-transform duration-700"
                                                    />
                                                </div>
                                                <div className={`absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-r ${section.gradient} opacity-20 blur-2xl rounded-full pointer-events-none`}></div>
                                                <div className={`absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-r ${section.gradient} opacity-20 blur-2xl rounded-full pointer-events-none`}></div>
                                            </div>
                                        </motion.div>

                                    </div>
                                </div>
                            </div>
                        </motion.section>
                    );
                })}
            </div>

            {/* Bottom Gradient */}
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent"></div>
        </div>
    );
}